import { lazy, Suspense, useEffect, useRef } from 'react';

import { useAuthUser } from '@/hooks/useAuthUser';
import useCredits from '@/hooks/useCredits';
import { useIsMobile } from '@/hooks/useIsMobile';
import { useResumePendingGenerations } from '@/hooks/useResumePendingGenerations';
import { calculateWebhook } from '@/services/pluggableApiService';
import { getAppStore } from '@/store';
import { getAvailableCredits } from '@/utils/creditUtils';

import type { AIWardrobeProps } from './types';

const DesktopLayout = lazy(() => import('@/components/layouts/Desktop').then(m => ({ default: m.DesktopLayout })));
const MobileLayout = lazy(() => import('@/components/layouts/Mobile').then(m => ({ default: m.MobileLayout })));

export function AIWardrobe({ onClose }: AIWardrobeProps) {
  const isMobile = useIsMobile();
  const { isAuthorized } = useAuthUser();
  const prevIsAuthorized = useRef<boolean | null>(null);
  const calcWebhookDone = useRef(false);
  useCredits();
  useResumePendingGenerations();

  useEffect(() => {
    if (calcWebhookDone.current || !isAuthorized) return;
    calculateWebhook()
      .then(ok => {
        if (ok) calcWebhookDone.current = true;
      })
      .catch(() => {});
  }, [isAuthorized]);

  // Auth-driven library sync — uses React state so transient token refreshes are batched out
  useEffect(() => {
    const prev = prevIsAuthorized.current;
    prevIsAuthorized.current = isAuthorized;

    if (prev === null) {
      // First render (e.g. page reload): if already logged in, load library and
      // refresh credits — otherwise availableCredits stays at its 0 default.
      if (isAuthorized) {
        getAppStore().loadUserLibrary();
        getAvailableCredits().catch(() => {});
      }
      return;
    }

    if (!isAuthorized && prev === true) {
      // Confirmed logout: clear user state
      getAppStore().resetUserState();
      return;
    }

    if (isAuthorized && prev === false) {
      // Confirmed login: load library and refresh credits
      getAppStore().loadUserLibrary();
      getAvailableCredits().catch(() => {});
    }
  }, [isAuthorized]);

  return <Suspense fallback={null}>{isMobile ? <MobileLayout onClose={onClose} /> : <DesktopLayout onClose={onClose} />}</Suspense>;
}
